import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDraftRegistry } from "./draftRegistry";

let groupSeq = 0;

const STATUS_TEXT = { dirty: "Not saved", saving: "Saving…", saved: "Saved", error: "Failed" };

/** Small per-line save status chip. */
export function RowStatus({ status }) {
  if (!status || status === "idle") return null;
  return (
    <span className={`eg-rowstatus is-${status}`} role="status" data-testid="row-status">
      {STATUS_TEXT[status]}
    </span>
  );
}

/**
 * One spreadsheet row (a <tbody> so a row can own an expander sub-row).
 *
 * Contract:
 * - `value` is the saved baseline; the row keeps its own draft while editing.
 * - Leaving the row (focus moves outside the tbody) or pressing Enter commits
 *   once, via onSave(draft). New rows also commit on blur once isComplete(draft)
 *   is true (Enter still works). Esc discards the draft. Unchanged rows never save.
 * - Every row registers with the editor's draft registry so pending drafts are
 *   flushed before a feature switch / leave, and on unmount as a safety net.
 * - children receive `status`: idle | dirty (not saved) | saving | saved | error.
 * - Enter moves focus to the same column in the next row.
 * - onDraft(draft | null) mirrors the in-progress values upward for the live summary.
 * - Failed saves keep the draft and show the error inline.
 */
export function EditableGroup({
  value,
  isNew = false,
  disabled = false,
  isComplete,
  onSave,
  onDraft,
  onStatus,
  children,
  className = "",
  testId,
}) {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const baseline = useRef(value);
  const dirtyRef = useRef(false);
  const draftRef = useRef(value);
  const rootRef = useRef(null);
  const busyRef = useRef(false);
  const inflight = useRef(null);
  const [status, setStatus] = useState("idle");
  const registry = useDraftRegistry();

  // Re-sync from the saved value unless the user is mid-edit.
  const valueKey = JSON.stringify(value);
  useEffect(() => {
    baseline.current = value;
    if (!dirtyRef.current) {
      draftRef.current = value;
      setDraft(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const update = useCallback(
    (patch) => {
      const next = { ...draftRef.current, ...patch };
      draftRef.current = next;
      dirtyRef.current = JSON.stringify(next) !== JSON.stringify(baseline.current);
      setDraft(next);
      setError("");
      setStatus(dirtyRef.current ? "dirty" : "idle");
      onDraft?.(dirtyRef.current ? next : null);
      registry?.note?.();
    },
    [onDraft, registry],
  );

  const cancel = useCallback(() => {
    dirtyRef.current = false;
    draftRef.current = baseline.current;
    setDraft(baseline.current);
    setError("");
    setStatus("idle");
    onDraft?.(null);
    registry?.note?.();
  }, [onDraft, registry]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const safeSet = (fn) => { if (mounted.current) fn(); };

  /** force=true (Enter) saves even an incomplete row so the helper's validation message shows. */
  const commit = useCallback(async (force = false) => {
    if (busyRef.current) return inflight.current || true;
    if (!dirtyRef.current) return true;
    if (force !== true && isComplete && !isComplete(draftRef.current)) return false;
    busyRef.current = true;
    safeSet(() => { setBusy(true); setStatus("saving"); });
    onStatus?.("saving");
    const run = (async () => {
      try {
        await onSave(draftRef.current);
        dirtyRef.current = false;
        onDraft?.(null);
        if (isNew) {
          draftRef.current = baseline.current;
          safeSet(() => setDraft(baseline.current));
        }
        safeSet(() => setStatus(isNew ? "idle" : "saved"));
        onStatus?.("saved");
        return true;
      } catch (err) {
        const message = err?.message || "Unable to save.";
        safeSet(() => { setError(message); setStatus("error"); });
        onStatus?.("error", message);
        return false;
      } finally {
        busyRef.current = false;
        inflight.current = null;
        safeSet(() => setBusy(false));
        registry?.note?.();
      }
    })();
    inflight.current = run;
    return run;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, isNew, onDraft, onSave, onStatus, registry]);

  // Register with the editor so drafts are flushed before switching / leaving.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    if (!registry) return undefined;
    const id = `eg-${(groupSeq += 1)}`;
    const unregister = registry.register(id, {
      isDirty: () => dirtyRef.current || busyRef.current,
      flush: () => commitRef.current(),
    });
    return () => {
      unregister();
      // Safety net: an unmount with a pending complete draft still saves.
      if (dirtyRef.current && !busyRef.current) commitRef.current();
    };
  }, [registry]);

  function handleBlur(event) {
    if (disabled) return;
    const next = event.relatedTarget;
    if (next && rootRef.current?.contains(next)) return;
    commit(true);
  }

  async function handleKeyDown(event) {
    if (disabled || event.defaultPrevented) return;
    if (event.key === "Escape") {
      if (dirtyRef.current) {
        event.preventDefault();
        cancel();
      }
      event.target.blur?.();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey && event.target.tagName !== "BUTTON") {
      event.preventDefault();
      const col = event.target.getAttribute("data-col");
      const ok = await commit(true);
      if (!ok) return;
      if (isNew) return; // the parent re-focuses the fresh blank row
      const nextGroup = rootRef.current?.nextElementSibling;
      const target =
        (col && nextGroup?.querySelector?.(`[data-col="${col}"]`)) ||
        nextGroup?.querySelector?.("input,select");
      target?.focus?.();
    }
  }

  const rowProps = {
    ref: rootRef,
    className: ["eg-group", isNew ? "is-new" : "", error ? "has-error" : "", busy ? "is-busy" : "", className]
      .filter(Boolean)
      .join(" "),
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    "data-testid": testId,
  };

  return (
    <tbody {...rowProps}>
      {children({ draft, update, cancel, commit, busy, error, disabled, status })}
      {error ? (
        <tr className="eg-error-row">
          <td colSpan={9} role="alert">
            {error}
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}
